import React from 'react'
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import {
  getDocumentTemplate,
  interpolate,
  RBC_BRANDING,
  type DocumentSectionTemplate,
} from '../lib/proposalDocumentTemplates'
import { buildDocumentTokens, formatLongDate, type DocumentRecord } from '../lib/documentTokens'
import {
  buildCompensation,
  type CompensationLineItem,
} from '../lib/documentCompensation'
import type { DocumentData, WorkSpecRow } from '../types/agreement'

export interface ProposalDocumentProps {
  phase: 'proposal' | 'contract'
  serviceType: string
  documentNumber?: string
  title?: string
  documentData: DocumentData
  lineItems: CompensationLineItem[]
  record: DocumentRecord
  scopeOfWork?: string
  salesTaxStatus?: string
  taxRate?: number
  monthlyAmount?: number
}

const BRAND_BLUE = '#168EB8'
const INK = '#1a1a1a'
const MUTED = '#555555'
const RULE = '#d6dde5'
const LOGO_URL = '/rbc-logo.png'

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingHorizontal: 46,
    paddingBottom: 72, // Add extra padding bottom so body text never overlaps the footer banner
    fontFamily: 'Times-Roman',
    fontSize: 10,
    color: INK,
    lineHeight: 1.45,
  },
  letterPage: {
    paddingTop: 54,
    paddingHorizontal: 54,
    paddingBottom: 64,
    fontFamily: 'Times-Roman',
    fontSize: 10.5,
    color: INK,
    lineHeight: 1.45,
  },
  // --- Cover page ---
  coverPage: {
    paddingTop: 42,
    paddingHorizontal: 54,
    paddingBottom: 50,
    fontFamily: 'Times-Roman',
    fontSize: 11,
    color: INK,
    lineHeight: 1.45,
    flexDirection: 'column',
    height: 792,
  },
  coverLogo: {
    width: 170,
    height: 46,
    marginBottom: 6,
  },
  coverDivider: {
    borderBottomWidth: 1.5,
    borderBottomColor: '#7a7a7a',
  },
  coverMain: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    paddingVertical: 16,
  },
  coverTitle: {
    fontSize: 22,
    fontFamily: 'Times-Bold',
    textAlign: 'center',
    textDecoration: 'underline',
  },
  coverBlock: {
    alignItems: 'center',
  },
  coverBlockLabel: {
    fontSize: 12,
    fontFamily: 'Times-Bold',
    textDecoration: 'underline',
    textAlign: 'center',
    marginBottom: 8,
  },
  coverCompanyName: {
    fontSize: 14,
    fontFamily: 'Times-Bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  coverBlockBody: {
    fontSize: 12,
    lineHeight: 1.35,
    textAlign: 'center',
  },
  coverRepBlock: {
    alignItems: 'center',
  },
  coverRepName: {
    fontSize: 12,
    fontFamily: 'Times-Bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  coverRepText: {
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 1.35,
  },
  coverFooterBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 34,
    backgroundColor: '#95C2DE',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  footerLogoText: {
    color: '#ffffff',
    fontFamily: 'Times-BoldItalic',
    fontSize: 18,
    width: 28,
  },
  footerInfoRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 8,
    paddingRight: 4,
  },
  footerInfoText: {
    color: '#ffffff',
    fontFamily: 'Times-Roman',
    fontSize: 9,
  },
  // --- Content pages ---
  letterLetterhead: {
    marginBottom: 20,
  },
  letterLetterheadTitle: {
    fontSize: 13,
    fontFamily: 'Times-Bold',
    color: INK,
    marginBottom: 2,
  },
  letterLetterheadText: {
    fontSize: 10,
    fontFamily: 'Times-Roman',
    color: INK,
    lineHeight: 1.3,
  },
  letterhead: {
    borderBottomWidth: 2,
    borderBottomColor: BRAND_BLUE,
    paddingBottom: 10,
    marginBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  logo: { width: 170, height: 46 },
  letterheadAddr: { fontSize: 8.5, color: MUTED, textAlign: 'right' },
  bodyTitle: {
    fontSize: 16,
    fontFamily: 'Times-Bold',
    color: BRAND_BLUE,
    textAlign: 'center',
    marginBottom: 14,
  },
  meta: { fontSize: 8.5, color: MUTED },
  docTitle: { fontSize: 17, fontFamily: 'Times-Bold', marginTop: 6 },
  docNumber: { fontSize: 9, color: MUTED, marginBottom: 12 },
  recipient: { fontSize: 10.5, marginBottom: 12 },
  salutation: { marginBottom: 8 },
  signoff: { marginTop: 14 },
  dateLine: { fontSize: 10.5, marginBottom: 12 },
  section: { marginBottom: 8 },
  sectionTitle: {
    fontSize: 12.5,
    fontFamily: 'Times-Bold',
    color: BRAND_BLUE,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
    paddingBottom: 3,
    marginBottom: 6,
  },
  paragraph: { marginBottom: 4 },
  statement: { fontFamily: 'Times-Bold', marginVertical: 3 },
  note: { fontSize: 9, color: MUTED, marginTop: 4 },
  subject: { fontFamily: 'Times-Bold', marginVertical: 8 },
  wsGroup: { fontFamily: 'Times-Bold', marginTop: 8, marginBottom: 3 },
  table: { borderWidth: 1, borderColor: RULE, marginTop: 5 },
  tr: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: RULE },
  trLast: { flexDirection: 'row' },
  th: {
    backgroundColor: '#f1f5f9',
    fontFamily: 'Times-Bold',
    fontSize: 9.5,
    padding: 4,
  },
  td: { fontSize: 9.5, padding: 4 },
  cellRight: { textAlign: 'right' },
  cellBorderLeft: { borderLeftWidth: 1, borderLeftColor: RULE },
  signatures: { flexDirection: 'row', marginTop: 18 },
  signCol: { flex: 1, paddingRight: 20 },
  signPartyName: { fontFamily: 'Times-Bold', fontSize: 10, marginBottom: 10 },
  signFieldRow: { flexDirection: 'row', marginBottom: 10, alignItems: 'flex-end' },
  signFieldLabel: { fontSize: 9.5, width: 36 },
  signFieldLine: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: '#333333',
    marginTop: 14,
    minHeight: 14,
  },
  footer: {
    position: 'absolute',
    bottom: 38, // Centered perfectly between bottom of content and blue banner
    left: 46,
    right: 46,
    textAlign: 'center',
    fontSize: 8,
    color: '#999999',
  },
})

function BrandFooter() {
  return (
    <View style={styles.coverFooterBanner} fixed>
      <Text style={styles.footerLogoText}>r</Text>
      <View style={styles.footerInfoRow}>
        <Text style={styles.footerInfoText}>670 Myrtle Ave #7348, Brooklyn NY 11205</Text>
        <Text style={styles.footerInfoText}>(718) 427-2111</Text>
        <Text style={styles.footerInfoText}>info@rbcfs.com</Text>
        <Text style={styles.footerInfoText}>www.rbcfs.com</Text>
      </View>
    </View>
  )
}

function CoverAddressLines({ address }: { address: string }) {
  const lines = (address || '').split('\n').filter((l) => l.trim())
  return (
    <>
      {lines.map((line, i) => (
        <Text key={i} style={styles.coverBlockBody}>{line}</Text>
      ))}
    </>
  )
}

function SignatureGrid({ leftName, rightName }: { leftName: string; rightName: string }) {
  const field = (label: string) => (
    <View style={styles.signFieldRow}>
      <Text style={styles.signFieldLabel}>{label}</Text>
      <View style={styles.signFieldLine} />
    </View>
  )
  return (
    <View style={styles.signatures} wrap={false}>
      <View style={styles.signCol}>
        <Text style={styles.signPartyName}>{leftName}</Text>
        {field('By:')}
        {field('Name:')}
        {field('Title:')}
        {field('Date:')}
      </View>
      <View style={styles.signCol}>
        <Text style={styles.signPartyName}>{rightName}</Text>
        {field('By:')}
        {field('Name:')}
        {field('Title:')}
        {field('Date:')}
      </View>
    </View>
  )
}

function Paragraphs({ text, style }: { text: string; style?: any }) {
  const blocks = (text || '').split('\n\n').filter((b) => b.trim() !== '')
  return (
    <>
      {blocks.map((b, i) => {
        const colonIndex = b.indexOf(':')
        if (colonIndex > 0 && colonIndex < 40 && !b.slice(0, colonIndex).includes('\n')) {
          const prefix = b.slice(0, colonIndex + 1)
          const rest = b.slice(colonIndex + 1)
          return (
            <Text key={i} style={[styles.paragraph, style].filter(Boolean) as any}>
              <Text style={{ fontFamily: 'Times-Bold' }}>{prefix}</Text>
              {rest}
            </Text>
          )
        }
        return (
          <Text key={i} style={[styles.paragraph, style].filter(Boolean) as any}>
            {b}
          </Text>
        )
      })}
    </>
  )
}

export const ProposalDocumentPdf: React.FC<ProposalDocumentProps> = ({
  phase,
  serviceType,
  title,
  documentData,
  lineItems,
  record,
  scopeOfWork,
  salesTaxStatus,
  monthlyAmount,
}) => {
  const template = getDocumentTemplate(serviceType)
  const tokens = buildDocumentTokens({ ...record, phase }, documentData || {})
  const compensation = buildCompensation({
    lineItems: lineItems || [],
    pricingMode: documentData?.pricingMode,
    contractType: record.contractType,
    salesTaxStatus,
    monthlyAmount,
    crew: documentData?.crew,
  })

  const overrides = documentData?.sections || []
  const isIncluded = (id: string) => {
    const o = overrides.find((s) => s.id === id)
    return o ? o.included !== false : true
  }
  const bodyText = (section: DocumentSectionTemplate) => {
    const o = overrides.find((s) => s.id === section.id)
    const raw = o?.textOverride && o.textOverride.trim() ? o.textOverride : section.defaultText || ''
    return interpolate(raw, tokens)
  }

  const workSpecs: WorkSpecRow[] =
    documentData?.workSpecs && documentData.workSpecs.length > 0
      ? documentData.workSpecs
      : template.defaultWorkSpecs

  const renderText = (section: DocumentSectionTemplate) => {
    const text = bodyText(section)
    if (!text.trim()) return null
    const blocks = text.split('\n\n').filter((b) => b.trim() !== '')
    if (blocks.length === 0) return null
    const firstBlock = blocks[0]
    const remainingBlocks = blocks.slice(1)
    return (
      <View key={section.id} style={styles.section}>
        <View wrap={false}>
          <Text style={styles.sectionTitle}>{section.heading}</Text>
          <Text style={styles.paragraph}>{firstBlock}</Text>
        </View>
        {remainingBlocks.map((b, i) => (
          <Text key={i} style={styles.paragraph}>
            {b}
          </Text>
        ))}
      </View>
    )
  }

  const renderCompensation = (section: DocumentSectionTemplate) => {
    const leadIn = bodyText(section)
    const leadInBlocks = leadIn.split('\n\n').filter((b) => b.trim() !== '')
    const hasLeadIn = leadInBlocks.length > 0
    const firstLeadIn = hasLeadIn ? leadInBlocks[0] : null
    const remainingLeadIn = hasLeadIn ? leadInBlocks.slice(1) : []

    return (
      <View key={section.id} style={styles.section}>
        <View wrap={false}>
          <Text style={styles.sectionTitle}>{section.heading}</Text>
          {firstLeadIn ? <Text style={styles.paragraph}>{firstLeadIn}</Text> : null}
        </View>
        {remainingLeadIn.map((b, i) => (
          <Text key={i} style={styles.paragraph}>
            {b}
          </Text>
        ))}
        {compensation.showRows && (
          <View style={styles.table}>
            <View style={styles.tr} wrap={false}>
              <Text style={[styles.th, { flex: 3 }]}>Description</Text>
              <Text style={[styles.th, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>
                {compensation.mode === 'rate_card' ? 'Rate' : 'Amount'}
              </Text>
              <Text style={[styles.th, styles.cellBorderLeft, { flex: 1 }]}>Unit</Text>
            </View>
            {compensation.rows.map((r, i) => {
              const last = !compensation.showTotal && i === compensation.rows.length - 1
              return (
                <View key={i} style={last ? styles.trLast : styles.tr} wrap={false}>
                  <Text style={[styles.td, { flex: 3 }]}>{r.label}</Text>
                  <Text style={[styles.td, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>
                    {r.amount}
                  </Text>
                  <Text style={[styles.td, styles.cellBorderLeft, { flex: 1 }]}>{r.unit}</Text>
                </View>
              )
            })}
            {compensation.showTotal && (
              <View style={styles.trLast} wrap={false}>
                <Text style={[styles.td, { flex: 3, fontFamily: 'Times-Bold' }]}>Total</Text>
                <Text
                  style={[
                    styles.td,
                    styles.cellRight,
                    styles.cellBorderLeft,
                    { flex: 1, fontFamily: 'Times-Bold' },
                  ]}
                >
                  {compensation.totalFormatted}
                </Text>
                <Text style={[styles.td, styles.cellBorderLeft, { flex: 1 }]}> </Text>
              </View>
            )}
          </View>
        )}
        {compensation.statements.map((s, i) => (
          <Text key={i} style={styles.statement}>
            {s}
          </Text>
        ))}
        {scopeOfWork && scopeOfWork.trim() ? <Paragraphs text={scopeOfWork} /> : null}
        {compensation.notes.map((n, i) => (
          <Text key={i} style={styles.note}>
            {n}
          </Text>
        ))}
        {documentData?.pw?.ptoHolidayBilledSeparately ? (
          <Text style={styles.note}>PTO and holiday pay are billed separately as incurred.</Text>
        ) : null}
        {documentData?.pw?.rateExpiryDate ? (
          <Text style={styles.note}>
            Rates valid until {formatLongDate(documentData.pw.rateExpiryDate)}.
          </Text>
        ) : null}
      </View>
    )
  }

  const renderWorkSpecs = (section: DocumentSectionTemplate) => {
    if (!workSpecs || workSpecs.length === 0) return null
    const groups: { group: string; rows: WorkSpecRow[] }[] = []
    for (const row of workSpecs) {
      const g = row.group || 'Specifications'
      let bucket = groups.find((x) => x.group === g)
      if (!bucket) {
        bucket = { group: g, rows: [] }
        groups.push(bucket)
      }
      bucket.rows.push(row)
    }
    const showFrequency = workSpecs.some((r) => (r.frequency || '').trim() !== '')
    return (
      <View key={section.id} style={styles.section}>
        <Text style={styles.sectionTitle}>{section.heading}</Text>
        {groups.map((grp, gi) => (
          <View key={gi} wrap={false}>
            <Text style={styles.wsGroup}>{grp.group}</Text>
            <View style={styles.table}>
              <View style={styles.tr}>
                <Text style={[styles.th, { flex: 1 }]}>Task</Text>
                {showFrequency && (
                  <Text style={[styles.th, styles.cellBorderLeft, { width: '32%' }]}>Frequency</Text>
                )}
              </View>
              {grp.rows.map((r, ri) => {
                const last = ri === grp.rows.length - 1
                return (
                  <View key={r.id} style={last ? styles.trLast : styles.tr}>
                    <Text style={[styles.td, { flex: 1 }]}>{r.task}</Text>
                    {showFrequency && (
                      <Text style={[styles.td, styles.cellBorderLeft, { width: '32%' }]}>
                        {r.frequency || '—'}
                      </Text>
                    )}
                  </View>
                )
              })}
            </View>
          </View>
        ))}
      </View>
    )
  }

  const renderSignature = (section: DocumentSectionTemplate) => (
    <View key={section.id} style={styles.section} wrap={false}>
      <Text style={styles.sectionTitle}>{section.heading}</Text>
      <Paragraphs text={bodyText(section)} />
      <SignatureGrid
        leftName={RBC_BRANDING.companyName}
        rightName={tokens.customerCompany}
      />
    </View>
  )

  const bodySections = template.sections.filter((s) => s.id !== 'work_specifications')
  const workSpecSection = template.sections.find((s) => s.id === 'work_specifications')
  const showWorkSpecs =
    workSpecSection &&
    isIncluded('work_specifications') &&
    workSpecs &&
    workSpecs.length > 0

  const docTitle = title || template.documentTitle
  const articleWord = phase === 'contract' ? 'agreement' : 'proposal'

  return (
    <Document title={docTitle} author={RBC_BRANDING.companyName}>
      {/* Page 1 - Cover */}
      <Page size="LETTER" style={styles.coverPage}>
        {/* Logo left-aligned */}
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <Image style={styles.coverLogo} src={LOGO_URL} />
        <View style={styles.coverDivider} />

        <View style={styles.coverMain}>
          <Text style={styles.coverTitle}>{template.documentTitle}</Text>

          <View style={styles.coverBlock}>
            <Text style={styles.coverBlockLabel}>Prepared for:</Text>
            <Text style={styles.coverCompanyName}>{tokens.customerCompany}</Text>
            <CoverAddressLines address={tokens.customerAddress} />
          </View>

          <View style={styles.coverBlock}>
            <Text style={styles.coverBlockLabel}>Submitted By:</Text>
            <Text style={styles.coverCompanyName}>{RBC_BRANDING.companyName}</Text>
            {RBC_BRANDING.companyAddressLines.map((line, i) => (
              <Text key={i} style={styles.coverBlockBody}>{line}</Text>
            ))}
          </View>

          <View style={styles.coverRepBlock}>
            <Text style={styles.coverRepName}>{RBC_BRANDING.repName}</Text>
            <Text style={styles.coverRepText}>{RBC_BRANDING.repPhone}</Text>
            <Text style={styles.coverRepText}>{RBC_BRANDING.repEmail}</Text>
          </View>
        </View>

        <BrandFooter />
      </Page>

      {/* Page 2 - Cover Letter */}
      <Page size="LETTER" style={styles.letterPage}>
        {/* Plain text letterhead */}
        <View style={styles.letterLetterhead}>
          <Text style={styles.letterLetterheadTitle}>{RBC_BRANDING.companyName}</Text>
          <Text style={styles.letterLetterheadText}>{RBC_BRANDING.companyAddressLines[0]}</Text>
          <Text style={styles.letterLetterheadText}>{RBC_BRANDING.companyAddressLines[1]}</Text>
        </View>

        {/* Date */}
        {record.date ? <Text style={styles.dateLine}>{formatLongDate(record.date)}</Text> : null}

        {/* Recipient */}
        <Text style={styles.recipient}>
          {tokens.customerName && tokens.customerName !== tokens.customerCompany
            ? `${tokens.customerName}\n`
            : ''}
          {tokens.customerCompany}
          {tokens.customerAddress ? `\n${tokens.customerAddress}` : ''}
        </Text>

        {/* Salutation */}
        <Text style={styles.salutation}>Dear {tokens.contactFirstName || 'Valued Customer'},</Text>
        
        {/* Subject line (with project/property address) */}
        <Text style={styles.subject}>Subject: {tokens.subjectLine}</Text>

        {/* Letter body */}
        <Text style={styles.paragraph}>
          Thank you for allowing {RBC_BRANDING.companyName} to prepare this {articleWord} for your
          consideration. We know it takes considerable time and effort to provide any potential
          vendor with all necessary information. So again, thanks!
        </Text>
        <Text style={styles.statement}>Here are a few important highlights:</Text>
        {template.introParagraphs.map((p, i) => (
          <Text key={i} style={styles.paragraph}>
            {interpolate(p, tokens)}
          </Text>
        ))}
        <Text style={styles.paragraph}>
          We look forward to the opportunity of becoming a trusted and valued partner. Please call if
          you have any questions, or need additional information as you review our {articleWord}.
        </Text>
        
        {/* Signoff */}
        <View style={styles.signoff}>
          <Text>Sincerely,</Text>
          <Text style={{ fontFamily: 'Times-Bold', marginTop: 10 }}>
            {RBC_BRANDING.repName} - {RBC_BRANDING.companyName}
          </Text>
        </View>

        <BrandFooter />
      </Page>

      {/* Page 3+ - Agreement Body and Specifications */}
      <Page size="LETTER" style={styles.page}>
        <Text style={styles.bodyTitle}>{template.bodyTitle}</Text>
        {bodySections.map((section) => {
          if (!isIncluded(section.id)) return null
          switch (section.kind) {
            case 'compensation':
              return renderCompensation(section)
            case 'workspecs':
              return null
            case 'signature':
              return renderSignature(section)
            default:
              return renderText(section)
          }
        })}
        {showWorkSpecs && workSpecSection ? (
          <View break>{renderWorkSpecs(workSpecSection)}</View>
        ) : null}

        <BrandFooter />

        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  )
}

export default ProposalDocumentPdf
